import { Tag, TagType } from '../types/tag';
import { MOCK_TAGS } from '../constants/tags';

const SIMULATED_DELAY_MS = 300;

/**
 * Simulate an API call delay
 */
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const tagService = {
    /**
     * Get all tags of a specific type
     */
    getTagsByType: async (type: TagType): Promise<Tag[]> => {
        await delay(SIMULATED_DELAY_MS);
        return MOCK_TAGS.filter((tag) => tag.type === type);
    },

    /**
     * Search tags by name (case-insensitive) across all types or specific types
     */
    /**
     * Search tags by name (case-insensitive) across all types or specific types
     * Returns top 5 matches sorted by popularity (usageCount)
     */
    searchTags: async (keyword: string, type?: TagType): Promise<Tag[]> => {
        await delay(SIMULATED_DELAY_MS);
        const lowerKeyword = keyword.toLowerCase();

        const matches = MOCK_TAGS.filter((tag) => {
            const matchesType = type ? tag.type === type : true;
            const matchesKeyword = tag.name.toLowerCase().includes(lowerKeyword);
            return matchesType && matchesKeyword;
        });

        // Sort by usageCount desc, then by name asc
        return matches.sort((a, b) => {
            const countA = a.usageCount || 0;
            const countB = b.usageCount || 0;
            if (countA !== countB) return countB - countA;
            return a.name.localeCompare(b.name);
        }).slice(0, 5); // Limit to top 5
    },

    /**
     * Get top N popular tags of a specific type
     */
    getTopTags: async (type: TagType, limit: number = 10): Promise<Tag[]> => {
        await delay(SIMULATED_DELAY_MS);
        return MOCK_TAGS
            .filter(t => t.type === type)
            .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
            .slice(0, limit);
    },

    /**
     * Mock creating a new tag (in a real app this would save to backend)
     * This simply returns a new Tag object correctly typed.
     */
    createTag: async (name: string, type: TagType): Promise<Tag> => {
        await delay(SIMULATED_DELAY_MS);
        const newTag: Tag = {
            id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: name.trim(),
            type,
            usageCount: 0
        };
        // In a real mock with persistence, we might push to MOCK_TAGS here
        // MOCK_TAGS.push(newTag); 
        return newTag;
    }
};
